import { useCallback, useEffect, useRef, useState } from 'react';
import {
  searchBacklogIssues,
  type BacklogProjectMeta,
  type BacklogSearchResultItem,
  type SearchBacklogIssuesFilter,
  type SearchBacklogIssuesResult,
} from '../lib/backlogSearch';

export interface UseBacklogSearchState {
  data: BacklogSearchResultItem[];
  /** key→name lookup from the most recent successful response. */
  backlogProjects: BacklogProjectMeta[];
  loading: boolean;
  /** Server-side structured failure (config / secret / Backlog API). */
  failure:
    | { reason: 'config-missing' | 'secret-error' | 'backlog-error'; message: string }
    | null;
  /** Client-side network/auth error. */
  error: Error | null;
  /** Trigger an explicit refetch with the current filter (e.g. after an action). */
  refresh: () => void;
}

/**
 * Fetches the realtime Backlog suggestion list. Refires whenever
 * `filter` changes by deep-stringify (the filter object is built fresh
 * from URL state each render, so a referential check would refetch on
 * every render). The keyword should already be debounced by the caller
 * — this hook doesn't second-guess input pacing.
 */
export function useBacklogSearch(
  projectId: string | null,
  filter: SearchBacklogIssuesFilter,
): UseBacklogSearchState {
  const [data, setData] = useState<BacklogSearchResultItem[]>([]);
  const [backlogProjects, setBacklogProjects] = useState<BacklogProjectMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<UseBacklogSearchState['failure']>(null);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  // Serialize filter so structural changes (not identity) drive refetch.
  // Keyword inputs that produce identical filters skip the network hit.
  const filterKey = JSON.stringify(filter);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  // Avoid setting state on an unmounted component / on a stale request
  // when filters change mid-flight.
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!projectId) {
      setData([]);
      setBacklogProjects([]);
      setLoading(false);
      setFailure(null);
      setError(null);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    setFailure(null);
    void (async () => {
      try {
        const result: SearchBacklogIssuesResult = await searchBacklogIssues({
          projectId,
          filter: filterRef.current,
        });
        if (reqId !== reqIdRef.current) return;
        if (result.ok) {
          setData(result.results);
          setBacklogProjects(result.backlogProjects);
          setFailure(null);
        } else {
          setData([]);
          setBacklogProjects([]);
          setFailure({ reason: result.reason, message: result.message });
        }
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setData([]);
        setBacklogProjects([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    })();
    // We intentionally use filterKey (stringified) so structurally
    // equal filters do not retrigger fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filterKey, nonce]);

  const refresh = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  return { data, backlogProjects, loading, failure, error, refresh };
}
